import { useTheme } from '@mui/material';
import { portalPaths, UrlSegments } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-querybuilder';
import { ClientMenu } from 'src/components/ClientComponents/client-menu';
import {
  EntityDetachedPanel,
  EntityMobilePeek,
  EntityPeekSlot,
  useEntityRecordClick,
} from 'src/components/EntitySidePanel';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { Client } from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { ClientSidePanel } from 'src/pages/Clients/SidePanel/side-panel';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';
import { useSelectedEntityParams } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-entity-params';
import { TabType, useCustomView, useOnEsc } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

// Mobile peek: the panel is a bare card column, so the host owns the scroller and
// the sticky tools slot (mirrors the mobile branch of ClientView/client-expanded.tsx).
const ClientMobilePanel = ({ client, onClose }: { client: Client; onClose: () => void }) => (
  <EntityMobilePeek>
    {(toolsSlot) => (
      <ClientSidePanel clientId={client.clientId} onClose={onClose} toolsSlot={toolsSlot} />
    )}
  </EntityMobilePeek>
);

interface ClientDetachedPanelProps {
  client: Client;
  onClose: () => void;
  customViewId?: string | null;
}

const ClientDetachedPanel = ({ client, onClose, customViewId }: ClientDetachedPanelProps) => {
  const { navigate } = useNavigateRoute();
  const theme = useTheme();

  return (
    <EntityDetachedPanel
      icon="user"
      entityColor={theme.palette.custom.OV.entity.client}
      title={client.fullName}
      detailsCardTestId="client-details-card"
      resetKey={client.clientId}
      closeTooltip="Close Client"
      menu={<ClientMenu clientId={client.clientId} />}
      onOpenFullPage={() => {
        navigate(portalPaths.client.full(client.clientId), { state: { customViewId } });
      }}
      onClose={onClose}
    >
      {(toolsSlot) => (
        <ClientSidePanel clientId={client.clientId} onClose={onClose} toolsSlot={toolsSlot} />
      )}
    </EntityDetachedPanel>
  );
};

export const ClientSelectedView = () => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const [selectedRecord, setSelectedRecord] = useState<Client | undefined>();
  const theme = useTheme();

  const customViewId = useCustomView(EntityNames.Client)?.customViewId;

  const {
    record: client,
    recordId: clientId,
    openRecord,
    closeRecord,
    customView,
    viewId,
    selectedCustomViewId,
  } = useSelectedEntityParams({ entityName: EntityNames.Client });

  const selectedClient = useMemo(
    () => (clientId ? ((client ?? selectedRecord) as Client) : undefined),
    [clientId, client, selectedRecord],
  );

  const prevClient = usePrevious(client);

  const onPanelClose = useCallback(() => {
    closeRecord();
    setSelectedRecord(undefined);
  }, [closeRecord]);

  useEffect(() => {
    // close panel if client was deleted
    if (selectedRecord && prevClient && prevClient?.id === selectedRecord.id && !client) {
      onPanelClose();
    }
  }, [client, selectedRecord, onPanelClose, prevClient]);

  useOnEsc(onPanelClose);

  const onRecordClick = useEntityRecordClick<Client>({
    customViewId,
    buildTabEntity: (client) => ({ entity: client, type: TabType.Client }),
    onOpenFullPage: (clientId) => {
      navigate(portalPaths.client.full(clientId), { state: { customViewId } });
    },
    onOpenRecord: (clientId, client) => openRecord(client ? client.clientId : clientId),
    onRecordSelect: setSelectedRecord,
  });

  return (
    <MetaTags title={selectedClient?.fullName || customView?.name || 'Clients'}>
      <BaseSelectedView<Client>
        title={selectedCustomViewId === UrlSegments.view.all ? 'All clients' : ''}
        entity={EntityNames.Client}
        viewId={viewId}
        selectedRecord={selectedClient}
        hasDetachedPanel={!isMobile}
        onRecordClick={onRecordClick}
        onRecordOpenFullPage={(clientId) => {
          navigate(portalPaths.client.full(clientId), { state: { customViewId } });
        }}
        onRecordClose={() => {
          closeRecord();
          setSelectedRecord(undefined);
        }}
        sx={{
          [theme.breakpoints.up(0)]: {
            maxWidth: '100%',
          },
        }}
        slots={{
          rightPanel: !!selectedClient && (
            <EntityPeekSlot open={!!selectedClient} isMobile={isMobile}>
              {isMobile ? (
                <ClientMobilePanel client={selectedClient} onClose={onPanelClose} />
              ) : (
                <ClientDetachedPanel
                  client={selectedClient}
                  onClose={onPanelClose}
                  customViewId={customViewId}
                />
              )}
            </EntityPeekSlot>
          ),
          createButton: <CreateButton icon="person_add" />,
        }}
      />
    </MetaTags>
  );
};
